import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getTabelaIndicadores } from '../../services/api';
import { formatarMoeda } from '../../utils/formatters';
import { serializarFiltros } from '../../utils/compararFiltros';

const INDICADORES = [
  { value: 'posto', label: 'Por Posto' },
  { value: 'produto', label: 'Por Produto' },
  { value: 'funcionario', label: 'Por Funcionário' },
  { value: 'cidade', label: 'Por Cidade' },
];

const TabelaAnalise = ({ filtros = {}, onCrossFilter, filtroCruzadoAtivo }) => {
  const [indicador, setIndicador] = useState('posto');
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handler para clique na linha
  const handleRowClick = (item) => {
    if (onCrossFilter) {
      onCrossFilter(indicador, item.nome);
    }
  };

  // Verificar se uma linha está selecionada pelo filtro cruzado
  const isRowSelected = (item) => {
    // filtroCruzadoAtivo agora é um objeto { posto: {...}, produto: {...}, ... }
    return filtroCruzadoAtivo?.[indicador]?.valor === item.nome;
  };

  // Memoizar a serialização dos filtros para evitar chamadas desnecessárias
  const filtrosSerializados = useMemo(() => serializarFiltros(filtros), [filtros]);
  const fetchTimeoutRef = useRef(null);

  useEffect(() => {
    // Limpar timeout anterior se existir
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Delay maior para dar tempo do prefetch completar (batch de 39 requests leva ~10s)
    // Se o prefetch já completou, o cache estará quente e retornará instantaneamente
    fetchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resultado = await getTabelaIndicadores(filtros, indicador);

        // Processar dados
        const dadosProcessados = resultado.map(item => ({
          nome: item['visao_geral.indicador_selecionado_posto_produto_fun'] || '-',
          margemLucro: parseFloat(item['visao_geral.margem_lucro'] || 0),
          lucro: parseFloat(item['visao_geral.lucro_total'] || 0),
          faturamento: parseFloat(item['visao_geral.valor_total'] || 0),
        }));

        // Ordenar por faturamento (maior para menor)
        dadosProcessados.sort((a, b) => b.faturamento - a.faturamento);

        setDados(dadosProcessados);
      } catch (error) {
        console.error('Erro ao buscar dados da tabela:', error);
        setDados([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms delay para dar tempo do prefetch completar

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [indicador, filtrosSerializados]);

  // Função para formatar porcentagem
  const formatarPorcentagem = (valor) => {
    return `${valor.toFixed(1)}%`;
  };

  // Função para obter o título da coluna baseado no indicador
  const getTituloColuna = () => {
    switch (indicador) {
      case 'posto':
        return 'Nome do posto';
      case 'produto':
        return 'Nome do produto';
      case 'funcionario':
        return 'Nome do funcionário';
      case 'cidade':
        return 'Nome da cidade';
      default:
        return 'Nome';
    }
  };

  return (
    <div className="dashboard-card">
      {/* Header */}
      <div className="flex flex-col items-start flex-shrink-0">
        <h3 className="dashboard-card-header">Análise faturamento/lucro</h3>
        <div className="dashboard-card-divider w-full"></div>
      </div>

      {/* Tabs de indicadores */}
      <div className="fluid-tabs text-green-500" style={{ marginBottom: 'var(--space-fluid-sm)' }}>
        {INDICADORES.map((item) => (
          <button
            key={item.value}
            onClick={() => setIndicador(item.value)}
            className={`fluid-tab ${indicador === item.value ? 'fluid-tab-active' : 'fluid-tab-inactive'}`}
          >
            {item.label}
            {indicador === item.value && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500"></div>
            )}
          </button>
        ))}
      </div>

      {/* Tabela com scroll horizontal em mobile */}
      <div className="flex-1 flex flex-col min-h-0 border border-gray-200 rounded-3xl overflow-hidden">
        {loading ? (
          <div className="fluid-spinner-container flex-1">
            <div className="flex flex-col items-center" style={{ gap: 'var(--space-fluid-sm)' }}>
              <div className="fluid-spinner"></div>
              <span className="fluid-spinner-text">Carregando dados...</span>
            </div>
          </div>
        ) : dados.length > 0 ? (
          <div className="flex-1 flex flex-col min-h-0 overflow-x-auto pl-2">
            {/* Tabela com largura mínima para scroll horizontal */}
            <table className="flex-shrink-0 mt-4" style={{ minWidth: '450px', width: '100%' }}>
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-gray-100">
                  <th className="fluid-table-header text-start font-extrabold text-black" style={{ minWidth: '120px', width: '30%' }}>
                    {getTituloColuna()}
                  </th>
                  <th className="fluid-table-header font-extrabold text-black" style={{ minWidth: '60px', width: '15%' }}>
                    Margem
                  </th>
                  <th className="fluid-table-header text-start font-extrabold text-black" style={{ minWidth: '100px', width: '27.5%' }}>
                    Lucro
                  </th>
                  <th className="fluid-table-header text-start font-extrabold text-black" style={{ minWidth: '110px', width: '27.5%' }}>
                    Faturamento
                  </th>
                </tr>
              </thead>
              <tbody>
                {dados.map((item, index) => (
                  <tr
                    key={index}
                    onClick={() => handleRowClick(item)}
                    className={`border-b border-gray-50 cursor-pointer transition-all duration-200
                      ${isRowSelected(item)
                        ? 'bg-orange-100 hover:bg-orange-200 ring-2 ring-orange-400 ring-inset'
                        : index % 2 === 0
                          ? 'bg-white hover:bg-blue-50'
                          : 'bg-gray-50 hover:bg-blue-50'
                      }`}
                    title={`Clique para filtrar por ${item.nome}`}
                  >
                    <td className={`fluid-table-cell font-medium ${isRowSelected(item) ? 'text-amber-800' : 'text-gray-800'}`} style={{ minWidth: '40px' }}>
                      <div className="truncate max-w-[120px] sm:max-w-[200px]">
                        {isRowSelected(item) && <span className="mr-1">✓</span>}
                        <span>{item.nome}</span>
                      </div>
                    </td>
                    <td className={`fluid-table-cell whitespace-nowrap ${isRowSelected(item) ? 'text-amber-700' : 'text-gray-600'}`} style={{ minWidth: '60px' }}>
                      {formatarPorcentagem(item.margemLucro)}
                    </td>
                    <td className={`fluid-table-cell whitespace-nowrap ${isRowSelected(item) ? 'text-amber-700' : 'text-gray-600'}`} style={{ minWidth: '100px' }}>
                      {formatarMoeda(item.lucro)}
                    </td>
                    <td className={`fluid-table-cell whitespace-nowrap ${isRowSelected(item) ? 'text-amber-700' : 'text-gray-600'}`} style={{ minWidth: '110px' }}>
                      {formatarMoeda(item.faturamento)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="w-full flex-1 flex items-center justify-center text-gray-500" style={{ fontSize: 'var(--text-fluid-sm)' }}>
            Nenhum dado disponível
          </div>
        )}
      </div>
    </div>
  );
};

export default TabelaAnalise;

